import { Calendar, DateSelectArg, EventClickArg, EventDropArg } from '@fullcalendar/core';
import frLocale from '@fullcalendar/core/locales/fr';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import { Modal } from 'bootstrap';
import Cookies from 'js-cookie';
import ky from 'ky';

const csrftoken = Cookies.get('csrftoken');

import type { CalendarEvent } from './types/calendar-event';
import type { Reservation } from './types/reservation';

interface Room {
  id: number;
  name: string;
  color: string;
  seats: number;
}

const formatEventsFromReservations = (reservations: Reservation[]): CalendarEvent[] => {
  return reservations.map((reservation: Reservation) => {
    return {
      id: reservation.id.toString(),
      title: reservation.title,
      start: `${reservation.date}T${reservation.startTime}`,
      end: `${reservation.date}T${reservation.endTime}`,
      resourceId: reservation.room.toString(),
      backgroundColor: reservation.color,
      borderColor: reservation.color,
      extendedProps: {
        room: reservation.room,
        user: reservation.user,
        canModify: reservation.canModify,
        extraUsers: reservation.extraUsers,
      },
    };
  });
};

const formatResources = (rooms: Room[]) => {
  return rooms.map((room) => ({
    id: room.id.toString(),
    title: room.name,
    extendedProps: {
      seats: room.seats,
      color: room.color,
    },
  }));
};

document.addEventListener('DOMContentLoaded', async () => {
  const calendarElement = document.getElementById('calendar');
  const reservationsElement = document.getElementById('reservations');

  if (!reservationsElement || !calendarElement) {
    return;
  }

  const reservations = JSON.parse(reservationsElement.textContent) as Reservation[];
  const events = formatEventsFromReservations(reservations);

  const isRoomView = calendarElement.dataset.roomView === 'true';
  const canAddEvent = calendarElement.dataset.canAddEvent === 'true';

  let rooms: Room[] = [];
  try {
    rooms = await ky.get('/api/rooms/').json<Room[]>();
  } catch (error) {
    console.error('Failed to fetch rooms:', error);
    return;
  }

  const resources = formatResources(rooms);

  const savedDate = Cookies.get('calendar_date');
  const initialDate = savedDate || undefined;

  const options = {
    plugins: [resourceTimeGridPlugin, interactionPlugin],
    locale: frLocale,
    initialView: isRoomView ? 'timeGridWeek' : 'resourceTimeGridDay',
    initialDate,
    weekends: false,
    slotMinTime: '08:00:00',
    slotMaxTime: '20:00:00',
    slotDuration: '00:15:00',
    headerToolbar: {
      left: 'prev,next today datePicker',
      center: 'title',
      right: isRoomView ? '' : 'resourceTimeGridDay,timeGridWeek',
    },
    views: {
      resourceTimeGridDay: {
        titleFormat: {
          weekday: 'long' as const,
          month: 'long' as const,
          day: 'numeric' as const,
          year: 'numeric' as const,
        },
      },
    },
    customButtons: {
      datePicker: {
        text: '📅',
        click: (): void => {
          const dateInput = document.getElementById('datePicker') as HTMLInputElement;
          if (dateInput) {
            dateInput.showPicker();
          }
        },
      },
    },
    allDaySlot: false,
    editable: canAddEvent,
    selectable: canAddEvent,
    selectMirror: true,
    slotEventOverlap: false,
    nowIndicator: true,
    eventDidMount: (info) => {
      info.el.title = info.event.title;
    },
    datesSet: (dateInfo) => {
      const year = dateInfo.start.getFullYear();
      const month = `0${dateInfo.start.getMonth() + 1}`.slice(-2);
      const day = `0${dateInfo.start.getDate()}`.slice(-2);
      const currentDate = `${year}-${month}-${day}`;
      const inFifteenMinutes = new Date(new Date().getTime() + 15 * 60 * 1000);
      Cookies.set('calendar_date', currentDate, { expires: inFifteenMinutes });
    },
    eventClassNames: (arg) => {
      if (arg.event.extendedProps.canModify) {
        return ['user-event'];
      }
      return [];
    },
    eventAllow: (_dropInfo, draggedEvent) => {
      return draggedEvent.extendedProps.canModify;
    },
    selectAllow: (selectInfo) => {
      return selectInfo.resource !== null;
    },
  };

  window.calendar = new Calendar(calendarElement, {
    ...options,
    resourceOrder: 'title',
    resources,
    events,
  });

  window.calendar.render();

  window.calendar.on('eventClick', async (event: EventClickArg) => {
    if (!event.event.extendedProps.canModify) {
      return;
    }

    const confirmDelete = window.confirm('Êtes-vous de vouloir annuler la réservation ?');

    if (confirmDelete) {
      await ky
        .delete(`/api/reservations/${event.event.id}/`, {
          headers: { 'X-CSRFToken': csrftoken },
        })
        .json();
      window.calendar.getEventById(event.event.id).remove();
    }
  });

  window.calendar.on('eventDrop', async (info: EventDropArg) => {
    if (!info.event.extendedProps.canModify) {
      info.revert();
      return;
    }

    const startDate = info.event.start;
    const endDate = info.event.end;

    if (!startDate || !endDate) {
      info.revert();
      return;
    }

    const date = startDate.toISOString().split('T')[0];
    const startTime = startDate.toTimeString().split(' ')[0];
    const endTime = endDate.toTimeString().split(' ')[0];

    const updateData: {
      date: string;
      startTime: string;
      endTime: string;
      room?: number;
    } = {
      date,
      startTime,
      endTime,
    };

    if (info.newResource) {
      updateData.room = Number.parseInt(info.newResource.id, 10);
    }

    try {
      await ky
        .patch(`/api/reservations/${info.event.id}/`, {
          headers: { 'X-CSRFToken': csrftoken },
          json: updateData,
        })
        .json();

      if (info.newResource) {
        const newRoomColor = info.newResource.extendedProps.color;
        info.event.setProp('backgroundColor', newRoomColor);
        info.event.setProp('borderColor', newRoomColor);
        info.event.setExtendedProp('room', updateData.room);
      }
    } catch (error) {
      alert('Erreur lors de la mise à jour de la réservation. Le créneau est peut-être déjà réservé.');
      info.revert();
    }
  });

  window.calendar.on('eventResize', async (info) => {
    if (!info.event.extendedProps.canModify) {
      info.revert();
      return;
    }

    const startDate = info.event.start;
    const endDate = info.event.end;

    if (!startDate || !endDate) {
      info.revert();
      return;
    }

    const date = startDate.toISOString().split('T')[0];
    const startTime = startDate.toTimeString().split(' ')[0];
    const endTime = endDate.toTimeString().split(' ')[0];

    const updateData = {
      date,
      startTime,
      endTime,
    };

    try {
      await ky
        .patch(`/api/reservations/${info.event.id}/`, {
          headers: { 'X-CSRFToken': csrftoken },
          json: updateData,
        })
        .json();
    } catch (error) {
      alert('Erreur lors de la mise à jour de la réservation. Le créneau est peut-être déjà réservé.');
      info.revert();
    }
  });

  window.calendar.on('select', (selectInfo: DateSelectArg) => {
    if (!selectInfo.resource && !calendarElement.dataset.roomId) {
      window.calendar.unselect();
      return;
    }

    const startDate = selectInfo.start;
    const endDate = selectInfo.end;

    const date = startDate.toISOString().split('T')[0];
    const startTime = startDate.toTimeString().split(' ')[0].substring(0, 5);
    const endTime = endDate.toTimeString().split(' ')[0].substring(0, 5);

    const dateInput = document.querySelector('#id_date') as HTMLInputElement;
    const startTimeInput = document.querySelector('#id_start_time') as HTMLInputElement;
    const endTimeInput = document.querySelector('#id_end_time') as HTMLInputElement;
    const roomInput = document.querySelector('#id_room') as HTMLSelectElement;

    if (dateInput && startTimeInput && endTimeInput) {
      dateInput.value = date;
      startTimeInput.value = startTime;
      endTimeInput.value = endTime;

      if (roomInput && selectInfo.resource) {
        roomInput.value = selectInfo.resource.id;
      } else if (roomInput && calendarElement.dataset.roomId) {
        roomInput.value = calendarElement.dataset.roomId;
      }

      const modalElement = document.querySelector('#addModal');
      if (modalElement) {
        const modal = new Modal(modalElement);
        modal.show();
      }
    }

    window.calendar.unselect();
  });

  const datePickerContainer = document.getElementById('datePickerContainer') as HTMLDivElement;

  // Move div adjacent to fc-datePicker-button class
  const datePickerButton = document.querySelector('.fc-datePicker-button') as HTMLButtonElement;
  if (datePickerButton) {
    datePickerButton.parentNode?.insertBefore(datePickerContainer, datePickerButton.nextSibling);
  }

  const datePickerInput = document.getElementById('datePicker') as HTMLInputElement;
  datePickerInput.addEventListener('change', (): void => {
    if (datePickerInput.value) {
      window.calendar.gotoDate(datePickerInput.value);
    }
  });
});
